import { stripEnvironmentalMetricsFromVector } from './cvss-environmental';
import type { CvssVersion } from './cvss-types';
import {
  calculateCvssBaseScoreFromVector,
  getCvssVersionFromVector,
} from './cvss-utils';

const CVSS_3_BASE_METRIC_KEYS = [
  'AV',
  'AC',
  'PR',
  'UI',
  'S',
  'C',
  'I',
  'A',
] as const;

export const CVSS_BASE_METRIC_KEYS = {
  '3.0': CVSS_3_BASE_METRIC_KEYS,
  '3.1': CVSS_3_BASE_METRIC_KEYS,
  '4.0': ['AV', 'AC', 'AT', 'PR', 'UI', 'VC', 'VI', 'VA', 'SC', 'SI', 'SA'],
} as const satisfies Record<CvssVersion, readonly string[]>;

export const CVSS_METRIC_REASON_SOURCES = [
  'AGENT',
  'USER',
  'PLATFORM',
] as const;

export type CvssMetricReasonSource =
  (typeof CVSS_METRIC_REASON_SOURCES)[number];

export type CvssMetricReason = {
  metric: string;
  value: string;
  reason: string;
  source: CvssMetricReasonSource;
};

export type CvssReasonsValidation =
  | { ok: true }
  | {
      ok: false;
      missingMetrics: string[];
      mismatchedMetrics: string[];
      duplicateMetrics: string[];
      explanationMissing: boolean;
    };

export type CvssMetricReasonDraft = CvssMetricReason & { carried: boolean };

const baseMetricKeysForVector = (vector: string): readonly string[] => {
  const version = getCvssVersionFromVector(vector);

  return version ? CVSS_BASE_METRIC_KEYS[version] : [];
};

export const parseCvssVectorMetricValues = (
  vector: string,
): Map<string, string> =>
  new Map(
    vector
      .split('/')
      .slice(1)
      .map((part) => part.split(':'))
      .filter(
        (pair): pair is [string, string] =>
          pair.length === 2 && pair[0] !== '' && pair[1] !== '',
      ),
  );

export const buildCvssVectorFromMetricValues = ({
  version,
  values,
}: {
  version: CvssVersion;
  values: ReadonlyMap<string, string>;
}): string | null => {
  const parts = CVSS_BASE_METRIC_KEYS[version].map((metric) => {
    const value = values.get(metric);

    return value ? `${metric}:${value}` : null;
  });

  if (parts.some((part) => part === null)) {
    return null;
  }

  const vector = `CVSS:${version}/${parts.join('/')}`;

  return calculateCvssBaseScoreFromVector(vector) === null ? null : vector;
};

const previousVectorOnSameVersion = ({
  previousVector,
  nextVector,
}: {
  previousVector: string | null | undefined;
  nextVector: string;
}): string | null => {
  if (!previousVector) {
    return null;
  }

  const previousVersion = getCvssVersionFromVector(
    stripEnvironmentalMetricsFromVector(previousVector),
  );
  const nextVersion = getCvssVersionFromVector(
    stripEnvironmentalMetricsFromVector(nextVector),
  );

  return previousVersion && previousVersion === nextVersion
    ? previousVector
    : null;
};

const metricValueUnchangedWithoutReason = ({
  previousValues,
  previousReasons,
  metric,
  nextValue,
}: {
  previousValues: ReadonlyMap<string, string> | null;
  previousReasons: readonly Pick<CvssMetricReason, 'metric' | 'reason'>[];
  metric: string;
  nextValue: string;
}): boolean =>
  previousValues !== null &&
  previousValues.get(metric) === nextValue &&
  !previousReasons.some(
    (reason) => reason.metric === metric && reason.reason.trim() !== '',
  );

export const requiresCvssVectorExplanation = ({
  previousVector,
  nextVector,
}: {
  previousVector: string | null | undefined;
  nextVector: string;
}): boolean =>
  !previousVector ||
  stripEnvironmentalMetricsFromVector(previousVector) !==
    stripEnvironmentalMetricsFromVector(nextVector);

export const cvssComponentExplanationMessage = (
  version: string | null | undefined,
): string =>
  version === '4.0'
    ? 'Add an explanation for the CVSS 4.0 component change.'
    : version === '3.1'
      ? 'Add an explanation for the CVSS 3.1 component change.'
      : 'Add a CVSS explanation.';

export const validateCvssReasons = ({
  vector,
  metricReasons,
  explanation,
  previousVector,
  previousReasons = [],
}: {
  vector: string;
  metricReasons: readonly Pick<
    CvssMetricReason,
    'metric' | 'value' | 'reason'
  >[];
  explanation: string | null | undefined;
  previousVector?: string | null;
  previousReasons?: readonly Pick<CvssMetricReason, 'metric' | 'reason'>[];
}): CvssReasonsValidation => {
  const baseVector = stripEnvironmentalMetricsFromVector(vector);
  const vectorValues = parseCvssVectorMetricValues(baseVector);
  const comparablePrevious = previousVectorOnSameVersion({
    previousVector,
    nextVector: vector,
  });
  const previousValues = comparablePrevious
    ? parseCvssVectorMetricValues(
        stripEnvironmentalMetricsFromVector(comparablePrevious),
      )
    : null;
  const metrics = baseMetricKeysForVector(baseVector);
  const reasonsByMetric = (metric: string) =>
    metricReasons.filter(
      (reason) => reason.metric === metric && reason.reason.trim() !== '',
    );

  const missingMetrics = metrics.filter(
    (metric) =>
      reasonsByMetric(metric).length === 0 &&
      !metricValueUnchangedWithoutReason({
        previousValues,
        previousReasons,
        metric,
        nextValue: vectorValues.get(metric) ?? '',
      }),
  );
  const duplicateMetrics = metrics.filter(
    (metric) => reasonsByMetric(metric).length > 1,
  );
  const mismatchedMetrics = metrics.filter((metric) =>
    reasonsByMetric(metric).some(
      (reason) => reason.value !== vectorValues.get(metric),
    ),
  );
  const explanationMissing =
    requiresCvssVectorExplanation({ previousVector, nextVector: vector }) &&
    (!explanation || explanation.trim() === '');

  if (
    missingMetrics.length === 0 &&
    duplicateMetrics.length === 0 &&
    mismatchedMetrics.length === 0 &&
    !explanationMissing
  ) {
    return { ok: true };
  }

  return {
    ok: false,
    missingMetrics,
    mismatchedMetrics,
    duplicateMetrics,
    explanationMissing,
  };
};

export const formatCvssReasonsError = (
  validation: Extract<CvssReasonsValidation, { ok: false }>,
  version?: string | null,
): string => {
  const reasonParts = [
    validation.missingMetrics.length > 0
      ? `Add a reason for: ${validation.missingMetrics.join(', ')}.`
      : null,
    validation.mismatchedMetrics.length > 0
      ? `Update the reason value to match the vector for: ${validation.mismatchedMetrics.join(', ')}.`
      : null,
    validation.duplicateMetrics.length > 0
      ? `Give only one reason for: ${validation.duplicateMetrics.join(', ')}.`
      : null,
  ].filter((part): part is string => part !== null);
  const explanationPart = validation.explanationMissing
    ? cvssComponentExplanationMessage(version)
    : null;

  return [
    reasonParts.length > 0 ? 'CVSS reasons are incomplete.' : null,
    ...reasonParts,
    explanationPart,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
};

export const isCvssBaseVectorChanged = ({
  previous,
  next,
}: {
  previous: string | null | undefined;
  next: string;
}): boolean =>
  !previous ||
  stripEnvironmentalMetricsFromVector(previous) !==
    stripEnvironmentalMetricsFromVector(next);

const reasonIsCarried = ({
  reason,
  previousReasons,
}: {
  reason: CvssMetricReason;
  previousReasons: readonly CvssMetricReason[];
}): boolean =>
  previousReasons.some(
    (previous) =>
      previous.metric === reason.metric &&
      previous.value === reason.value &&
      previous.reason === reason.reason &&
      previous.source === reason.source,
  );

export const resolveCvssMetricReasonDrafts = ({
  previousVector,
  previousReasons,
  nextVector,
  draftReasons,
}: {
  previousVector: string | null | undefined;
  previousReasons: readonly CvssMetricReason[];
  nextVector: string;
  draftReasons: readonly CvssMetricReason[];
}): CvssMetricReasonDraft[] => {
  const nextValues = parseCvssVectorMetricValues(
    stripEnvironmentalMetricsFromVector(nextVector),
  );
  const comparablePrevious = previousVectorOnSameVersion({
    previousVector,
    nextVector,
  });
  const previousValues = comparablePrevious
    ? parseCvssVectorMetricValues(
        stripEnvironmentalMetricsFromVector(comparablePrevious),
      )
    : null;

  return baseMetricKeysForVector(nextVector).map((metric) => {
    const value = nextValues.get(metric) ?? '';
    const draft = draftReasons.find(
      (reason) =>
        reason.metric === metric &&
        reason.value === value &&
        reason.reason.trim() !== '' &&
        !reasonIsCarried({ reason, previousReasons }),
    );
    const previous = previousReasons.find(
      (reason) =>
        reason.metric === metric &&
        reason.value === value &&
        reason.reason.trim() !== '',
    );

    if (draft) {
      return { ...draft, carried: false };
    }

    if (previous && previousValues?.get(metric) === value) {
      return { ...previous, carried: true };
    }

    return {
      metric,
      value,
      reason: '',
      source: 'USER',
      carried: metricValueUnchangedWithoutReason({
        previousValues,
        previousReasons,
        metric,
        nextValue: value,
      }),
    };
  });
};
